import type {
  ConnectContact,
  ConnectConnection,
  ConnectionState,
} from '@/types';

export type ConnectionWithEvents = ConnectConnection & {
  onConnected?: (callback: () => void) => void;
  onEnded?: (callback: () => void) => void;
  getStatus?: () => ConnectionState | undefined;
};

export type ThirdPartyLegFilter = 'outbound' | 'outbound_or_queue';

function matchesThirdPartyLegType(
  rawType: string,
  legFilter: ThirdPartyLegFilter
): boolean {
  const upper = rawType.toUpperCase();
  if (legFilter === 'outbound') return upper.includes('OUTBOUND');
  return upper.includes('OUTBOUND') || upper.includes('QUEUE');
}

/** Snapshot connection ids before addConnection (cold/warm dial). */
export function connectionIdsBeforeDial(contact: ConnectContact): Set<string> {
  return new Set(
    (contact.getConnections?.() ?? [])
      .map(c => c.getConnectionId?.())
      .filter((id): id is string => Boolean(id))
  );
}

/**
 * True when Connect has already ended a leg (status 'disconnected').
 * Streams keeps ended legs in contact.getConnections(), but the CCP rejects
 * destroyConnection on them — there is nothing left to destroy.
 */
function isThirdPartyLegEnded(conn: ConnectionWithEvents): boolean {
  try {
    if (conn.isConnected?.() || conn.isOnHold?.()) return false;
    const statusType = String(conn.getStatus?.()?.type ?? '').toLowerCase();
    return statusType === 'disconnected';
  } catch {
    return false;
  }
}

/**
 * Find agent-1's third-party leg (Teams PSTN, queue consult, etc.).
 * When `beforeConnectionIds` is set, only legs added after that snapshot match.
 * When `excludeEndedLegs` is set, legs Connect has already ended are skipped.
 */
export function findThirdPartyConnection(
  contact: ConnectContact,
  options: {
    beforeConnectionIds?: Set<string>;
    connectionId?: string | null;
    legFilter?: ThirdPartyLegFilter;
    excludeEndedLegs?: boolean;
  } = {}
): ConnectionWithEvents | undefined {
  const {
    beforeConnectionIds,
    connectionId = null,
    legFilter = 'outbound_or_queue',
    excludeEndedLegs = false,
  } = options;
  const agentConnectionId = contact.getAgentConnection?.()?.getConnectionId?.();
  const initialConnectionId = contact
    .getInitialConnection?.()
    ?.getConnectionId?.();
  const connections = contact.getConnections?.() ?? [];

  return connections.find(conn => {
    const id = conn.getConnectionId?.();
    if (!id || id === agentConnectionId || id === initialConnectionId) {
      return false;
    }
    if (connectionId) return id === connectionId;
    if (beforeConnectionIds?.has(id)) return false;
    const rawType = String(conn.getType?.() ?? '');
    if (!matchesThirdPartyLegType(rawType, legFilter)) return false;
    if (
      excludeEndedLegs &&
      isThirdPartyLegEnded(conn as ConnectionWithEvents)
    ) {
      return false;
    }
    return true;
  }) as ConnectionWithEvents | undefined;
}

/** @deprecated Use findThirdPartyConnection */
export const findWarmConsultConnection = (
  contact: ConnectContact,
  beforeConnectionIds: Set<string>
) =>
  findThirdPartyConnection(contact, {
    beforeConnectionIds,
    legFilter: 'outbound_or_queue',
  });

export function waitForThirdPartyConnectionConnected(
  contact: ConnectContact,
  beforeConnectionIds: Set<string>,
  options: {
    onConnected: () => void;
    onFailure: (reason: string) => void;
    maxPolls?: number;
    legFilter?: ThirdPartyLegFilter;
    /** When false, transient leg teardown during agent join is ignored. */
    failOnLegEnd?: boolean;
  }
): () => void {
  const maxPolls = options.maxPolls ?? 120;
  const legFilter = options.legFilter ?? 'outbound_or_queue';
  const failOnLegEnd = options.failOnLegEnd ?? true;
  let pollCount = 0;
  let settled = false;
  let outbound: ConnectionWithEvents | null = null;
  let handlersAttached = false;

  const cleanup = () => {
    settled = true;
    clearInterval(intervalId);
  };

  const onceConnected = () => {
    if (settled) return;
    cleanup();
    options.onConnected();
  };

  const onceFailure = (reason: string) => {
    if (settled) return;
    cleanup();
    options.onFailure(reason);
  };

  contact.onEnded?.(() => onceFailure('contact_ended'));

  const intervalId = setInterval(() => {
    pollCount += 1;
    try {
      if (!outbound) {
        outbound =
          findThirdPartyConnection(contact, {
            beforeConnectionIds,
            legFilter,
          }) ?? null;
      }
      if (outbound && !handlersAttached) {
        handlersAttached = true;
        outbound.onConnected?.(() => onceConnected());
        outbound.onEnded?.(() => {
          if (failOnLegEnd) onceFailure('consult_ended');
        });
      }
      if (!outbound) {
        if (pollCount >= maxPolls) onceFailure('consult_not_found');
        return;
      }
      if (
        outbound.isConnected?.() ||
        String(outbound.getStatus?.()?.type ?? '').toLowerCase() === 'connected'
      ) {
        onceConnected();
        return;
      }
      if (pollCount >= maxPolls) onceFailure('consult_timeout');
    } catch {
      onceFailure('consult_poll_error');
    }
  }, 500);

  return cleanup;
}

/** @deprecated Use waitForThirdPartyConnectionConnected */
export const waitForConsultConnected = waitForThirdPartyConnectionConnected;

/**
 * Watch an established third-party consult leg and notify when it ends or drops.
 * Used on Agent 1 after consult connects (private consult or conference).
 */
function isActiveThirdPartyLeg(conn: ConnectionWithEvents): boolean {
  try {
    return (
      conn.isConnected?.() ||
      conn.isOnHold?.() ||
      String(conn.getStatus?.()?.type ?? '').toLowerCase() === 'connected'
    );
  } catch {
    return false;
  }
}

function findActiveThirdPartyConnection(
  contact: ConnectContact,
  beforeConnectionIds: Set<string>,
  legFilter: ThirdPartyLegFilter
): ConnectionWithEvents | undefined {
  const agentConnectionId = contact.getAgentConnection?.()?.getConnectionId?.();
  const initialConnectionId = contact
    .getInitialConnection?.()
    ?.getConnectionId?.();

  return (contact.getConnections?.() ?? []).find(conn => {
    const id = conn.getConnectionId?.();
    if (!id || beforeConnectionIds.has(id)) return false;
    if (id === agentConnectionId || id === initialConnectionId) return false;
    const rawType = String(conn.getType?.() ?? '');
    if (!matchesThirdPartyLegType(rawType, legFilter)) return false;
    return isActiveThirdPartyLeg(conn as ConnectionWithEvents);
  }) as ConnectionWithEvents | undefined;
}

export function watchThirdPartyConnectionEnded(
  contact: ConnectContact,
  beforeConnectionIds: Set<string>,
  options: {
    onEnded: () => void;
    legFilter?: ThirdPartyLegFilter;
    /** Consecutive polls with no active consult leg before firing (default 8 = 4s). */
    absentPollsBeforeEnd?: number;
    /** Ignore disconnect while Connect reshapes legs after Agent 2 joins. */
    ignoreUntilMs?: number;
    /**
     * Start counting absent polls immediately, even if this watcher never
     * sees an active leg itself. Use when the caller just confirmed the leg
     * was connected (e.g. the leg ends between confirmation and first poll).
     */
    assumeActiveAtStart?: boolean;
  }
): () => void {
  const legFilter = options.legFilter ?? 'outbound_or_queue';
  const absentPollsBeforeEnd = options.absentPollsBeforeEnd ?? 8;
  const ignoreUntilMs = options.ignoreUntilMs ?? 0;
  let settled = false;
  let watchedConnectionId: string | null = null;
  let sawActiveLeg = options.assumeActiveAtStart ?? false;
  let absentPolls = 0;

  const cleanup = () => {
    settled = true;
    clearInterval(intervalId);
  };

  const onceEnded = () => {
    if (settled) return;
    if (ignoreUntilMs > 0 && Date.now() < ignoreUntilMs) return;
    cleanup();
    options.onEnded();
  };

  const attachEndedHandler = (conn: ConnectionWithEvents) => {
    const id = conn.getConnectionId?.() ?? null;
    if (!id || id === watchedConnectionId) return;
    watchedConnectionId = id;
    conn.onEnded?.(() => {
      if (!sawActiveLeg) return;
      // Leg swap when Agent 2 joins — wait for replacement, do not disconnect immediately.
      watchedConnectionId = null;
      absentPolls = 0;
    });
  };

  const intervalId = setInterval(() => {
    try {
      const activeLeg = findActiveThirdPartyConnection(
        contact,
        beforeConnectionIds,
        legFilter
      );

      if (activeLeg) {
        attachEndedHandler(activeLeg);
        sawActiveLeg = true;
        absentPolls = 0;
        return;
      }

      if (!sawActiveLeg) return;
      if (ignoreUntilMs > 0 && Date.now() < ignoreUntilMs) {
        absentPolls = 0;
        return;
      }

      absentPolls += 1;
      if (absentPolls >= absentPollsBeforeEnd) onceEnded();
    } catch {
      if (!sawActiveLeg) return;
      if (ignoreUntilMs > 0 && Date.now() < ignoreUntilMs) return;
      absentPolls += 1;
      if (absentPolls >= absentPollsBeforeEnd) onceEnded();
    }
  }, 500);

  return cleanup;
}
